import { Typography } from "@mui/material";
import { StyledEntries } from "@features/tickets/shared/pages/entries/entries.styled";
import { ItemContext } from "./ItemContext";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import { DataGrid, GridColDef, GridLocaleText, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { dateStringFormatPrinter } from "@shared/lib/utils";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { useTheme } from "@emotion/react";
import { useItemEntriesBrowse } from "@features/tickets/shared/hooks/useItemEntriesBrowse";

function parseItemId(value: string | undefined): number | undefined {
  if (!value || !/^\d+$/.test(value)) {
    return undefined;
  }

  const itemId = Number(value);

  return Number.isFinite(itemId) && itemId > 0 ? itemId : undefined;
}

function ItemEntries() {
  const theme = useTheme();
  const { id } = useParams();
  const { item } = useContext(ItemContext);
  const itemId = item?.id ?? parseItemId(id);

  const {
    data: rows,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    page,
    perPage,
    hasNextPage,
    handleNextPage,
    handlePrevPage,
    handleRowsPerPageChange,
  } = useItemEntriesBrowse(itemId);

  const customLocaleText: Partial<GridLocaleText> = {
    noRowsLabel: "אין רשומות.",
    columnMenuLabel: "תפריט",
    columnHeaderSortIconLabel: "מיון",
    columnMenuUnsort: "ביטול מיון",
    columnMenuSortAsc: "מיון א-ת 9-1",
    columnMenuSortDesc: "מיון ת-א 1-9",
    columnMenuFilter: "סינון",
    columnMenuHideColumn: "הסתר עמודה",
    columnMenuShowColumns: "הצג עמודות",
    columnMenuManageColumns: "ניהול עמודות",
    columnsManagementShowHideAllText: "הצג/הסתר הכל",
    columnsManagementReset: "איפוס",
    columnsManagementSearchTitle: "חיפוש",
    filterPanelOperator: "פעולה",
    filterPanelOperatorAnd: "ו-",
    filterPanelOperatorOr: "או",
    filterPanelColumns: "עמודה",
    filterPanelInputLabel: "ערך",
    filterPanelInputPlaceholder: "ערך לסינון",
    filterOperatorContains: "מכיל",
    filterOperatorEquals: "שווה",
    filterOperatorStartsWith: "מתחיל עם",
    filterOperatorEndsWith: "נגמר עם",
    filterOperatorIsEmpty: "הוא ריק",
    filterOperatorIsNotEmpty: "הוא לא ריק",
    filterOperatorIsAnyOf: "כל אחד מ",
    filterOperatorIs: "הוא",
    filterOperatorNot: "הוא לא",
    filterOperatorAfter: "הוא אחרי",
    filterOperatorOnOrAfter: "הוא מ",
    filterOperatorBefore: "הוא לפני",
    filterOperatorOnOrBefore: "הוא עד",
    noResultsOverlayLabel: "לא נמצאו תוצאות.",
    MuiTablePagination: {
      labelRowsPerPage: "מס שורות מקסימלי לעמוד:",
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}–${to} מתוך ${count !== -1 ? count : `יותר מ- ${to}`}`,
      backIconButtonProps: {
        sx: { transform: "rotate(180deg)" },
      },
      nextIconButtonProps: {
        sx: { transform: "rotate(180deg)" },
      },
    },
  };

  const columns: GridColDef[] = [
    { field: "created_at", headerName: "תאריך", width: 140, valueGetter: (value: string) => dateStringFormatPrinter(value) },
    { field: "sku", headerName: "מק\"ט", width: 160 },
    { field: "out", headerName: "יציאה", width: 120 },
    { field: "in", headerName: "כניסה", width: 120 },
    { field: "balance", headerName: "יתרה", width: 120 },
    { field: "price", headerName: "מחיר", width: 120, valueGetter: (value: number) => value ? `${Number(value).toFixed(2)}₪` : "" },
    { field: "account_name", headerName: "שם חשבון", width: 220 },
    { field: "account_number", headerName: "מספר חשבון", width: 160 },
  ];

  const rowCount = hasNextPage ? -1 : page * perPage + rows.length;
  const sortModel: GridSortModel = sortKey === "id"
    ? []
    : [{ field: sortKey, sort: sortMethod === "ASC" ? "asc" : "desc" }];

  function handlePaginationModelChange(model: GridPaginationModel) {
    if (model.pageSize !== perPage) {
      handleRowsPerPageChange(model.pageSize);
      return;
    }

    if (model.page > page) {
      handleNextPage();
      return;
    }

    if (model.page < page) {
      handlePrevPage();
    }
  }

  function handleSortModelChange(model: GridSortModel) {
    const sort = model[0];

    if (!sort) {
      handleSortKeyChanged("id");
      handleSortMethodChanged("DESC");
      return;
    }

    handleSortKeyChanged(sort.field);
    handleSortMethodChanged(sort.sort === "asc" ? "ASC" : "DESC");
  }

  if (!itemId) {
    return (
      <StyledEntries>
        <div className="container">
          <Typography variant="body1">לא נמצא פריט.</Typography>
          <div className="buttons">
            <ReturnButton />
          </div>
        </div>
      </StyledEntries>
    );
  }

  return (
    <StyledEntries>
      <div className="container">
        <div className="top">
          <div>
            <Typography variant="body1" component={"span"}>מק"ט:</Typography>
            <Typography variant="body2" component={"span"}>{item?.sku ?? "-"}</Typography>
          </div>
          <div>
            <Typography variant="body1" component={"span"}>שם פריט:</Typography>
            <Typography variant="body2" component={"span"}>{item?.name ?? "-"}</Typography>
          </div>
        </div>
        <DataGrid
          disableRowSelectionOnClick
          disableColumnFilter
          columns={columns}
          rows={rows}
          autoHeight
          loading={isLoading}
          paginationMode="server"
          sortingMode="server"
          rowCount={rowCount}
          paginationMeta={{ hasNextPage }}
          paginationModel={{ page, pageSize: perPage }}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          localeText={customLocaleText}
          sx={{
            "& .MuiDataGrid-topContainer svg": {
              color: "rgba(0,0,0,0.57)",
            },
            "& .MuiDataGrid-virtualScrollerContent, .MuiTablePagination-root, .MuiSvgIcon-root": {
              color: theme.colors.main_color,
            },
          }}
        />
        <div className="buttons">
          <ReturnButton />
        </div>
      </div>
    </StyledEntries>
  );
}

export default ItemEntries;
